/**
 * Answer summary table for normal questions
 * This table shows answers entered by users in
 * their questionnaire per question and case
 */

import React from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableCellProps,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { styled } from '@mui/material/styles';
import {
  DigitalCase,
  DigitalQuestion,
  DigitalSubmission,
  QuestionCaseSetting,
  QuestionTypeV2,
  RootState,
} from '../../redux/types';
import { formatNumber } from '../../utilities/numberHelper';
import { Colors } from '../utilities/Colors';
import Legend from './Legend';

type CellProps = {
  clickable?: boolean,
  warning?: boolean,
  dismissed?: boolean,
};

const StyledTableCell = styled<any>(TableCell)((
  props: CellProps & TableCellProps,
) => {
  if (props.clickable && props.warning) {
    return {
      backgroundColor: '#fdeded',
      lineBreak: 'strict',
      minWidth: '155px',
      '&:hover': {
        backgroundColor: 'ButtonFace',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
      },
    };
  } if (props.clickable) {
    return {
      lineBreak: 'strict',
      minWidth: '155px',
      '&:hover': {
        backgroundColor: 'ButtonFace',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
      },
    };
  } if (props.dismissed) {
    return {
      backgroundColor: '#f5f5f5',
      lineBreak: 'strict',
      minWidth: '155px',
    };
  }
  return {
    lineBreak: 'strict',
    minWidth: '155px',
  };
});

type CellAnswerProps = {
  answer?: string | JSX.Element;
  dismissed: boolean;
}

const CellAnswer: React.FC<CellAnswerProps> = ({
  answer,
  dismissed,
}: CellAnswerProps) => {
  if (dismissed) {
    return <span />;
  } if (answer === undefined) {
    return <b style={{ color: Colors.wrong }}>N/A</b>;
  }
  return <Box sx={{ whiteSpace: 'pre-wrap' }}>{answer}</Box>;
};

type Props = {
  digitalQuestions: DigitalQuestion[];
  digitalCases: DigitalCase[];
  submissionsMapByQuestionAndCaseId: Record<number, Record<number, DigitalSubmission> | undefined>;
  compulsorySettingsMapByQuestionAndCaseId?: Record<
    number, Record<number, QuestionCaseSetting> | undefined
  >;
  settingsDictionary?: { get: (qId: number, cId: number) => QuestionCaseSetting | undefined };
  showLegend?: boolean
  onCaseClick?: (questionId: number, caseId: number) => void;
};

function getAnswerStatusColor(status?: DigitalSubmission['scoreStatus']) {
  switch (status) {
    case 'correct':
      return { color: Colors.correct };
    case 'acceptable':
      return { color: Colors.acceptable };
    case 'wrong':
      return { color: Colors.wrong };
    default:
      return undefined;
  }
}

const AnswerSummary: React.FC<Props> = ({
  digitalQuestions,
  digitalCases,
  submissionsMapByQuestionAndCaseId,
  compulsorySettingsMapByQuestionAndCaseId: compulsorySettingsMap,
  settingsDictionary,
  showLegend,
  onCaseClick,
}: Props) => {
  const locale = useSelector((state: RootState) => state.intl.locale);
  if (!digitalQuestions.length || !digitalCases) {
    return null;
  }
  return (
    <Box>
      <TableContainer>
        <Table sx={{ position: 'relative', overflow: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: 'sticky',
                  backgroundColor: 'white',
                  left: 0,
                  width: 300,
                }}
              >
                <FormattedMessage id="answerSummary.questionTitle" />
              </TableCell>
              {
                digitalCases.map((dc) => (
                  <TableCell key={dc.id} align="center">{dc.name}</TableCell>
                ))
              }
            </TableRow>
          </TableHead>
          <TableBody>
            {
              digitalQuestions.map((dq) => (
                <TableRow key={dq.id}>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      backgroundColor: 'white',
                      left: 0,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{dq.text}</Typography>
                  </TableCell>
                  {
                    digitalCases.map((dc) => {
                      const dismissed = settingsDictionary?.get(dq.id, dc.id)?.hide;
                      const submissionObj = submissionsMapByQuestionAndCaseId[
                        dq.id]?.[dc.id];
                      let answer;
                      if (dq.type === QuestionTypeV2.NumberRange
                        && submissionObj?.type === QuestionTypeV2.NumberRange) {
                        if (submissionObj.answer === 'RTU' || submissionObj.answer === '-') {
                          answer = submissionObj.answer;
                        } else {
                          answer = formatNumber(locale, submissionObj.answer);
                        }
                      }
                      if (dq.type === QuestionTypeV2.SingleChoice
                        && submissionObj?.type === QuestionTypeV2.SingleChoice) {
                        const optionObj = dq.payload?.find((v) => v.id === submissionObj.answer);
                        answer = optionObj?.text;
                      }
                      if (dq.type === QuestionTypeV2.MultiChoice
                        && submissionObj?.type === QuestionTypeV2.MultiChoice) {
                        answer = dq.payload?.map((p) => (submissionObj.answer[p.id]
                          ? <li key={p.id}>{p.text}</li> : undefined))
                          .filter((a) => !!a);
                        answer = <ul style={{ listStylePosition: 'inside', padding: 0 }}>{answer}</ul>;
                      }
                      if (dq.type === QuestionTypeV2.Ratio
                        && submissionObj?.type === QuestionTypeV2.Ratio) {
                        answer = submissionObj.answer.join(' : ');
                      }
                      if (dq.type === QuestionTypeV2.String
                        && submissionObj?.type === QuestionTypeV2.String) {
                        answer = submissionObj.answer.answer;
                      }
                      if (dq.type === QuestionTypeV2.SingleChoiceWithText
                        && submissionObj?.type === QuestionTypeV2.SingleChoiceWithText) {
                        const optionObj = dq.payload?.find((v) => v.id in submissionObj.answer);
                        if (optionObj) {
                          const val = submissionObj.answer[optionObj.id]?.text;
                          answer = `${optionObj?.text} ${val ? `(${val})` : ''}`;
                        }
                      }
                      if (dq.type === QuestionTypeV2.MultiChoiceWithText
                        && submissionObj?.type === QuestionTypeV2.MultiChoiceWithText) {
                        answer = dq.payload?.map((p) => (submissionObj.answer[p.id]?.selected
                          ? (
                            <li key={p.id}>
                              {p.text}
                              {submissionObj.answer[p.id].text ? ` (${submissionObj.answer[p.id].text})` : ''}
                            </li>
                          ) : undefined))
                          .filter((a) => !!a);
                        answer = <ul style={{ listStylePosition: 'inside', padding: 0 }}>{answer}</ul>;
                      }

                      return (
                        <StyledTableCell
                          clickable={onCaseClick && !dismissed ? 'true' : undefined}
                          warning={compulsorySettingsMap?.[dq.id]?.[dc.id]?.compulsory ? 'true' : undefined}
                          key={dc.id}
                          align="center"
                          onClick={
                            !onCaseClick || dismissed ? undefined : () => onCaseClick(dq.id, dc.id)
                          }
                          dismissed={dismissed ? 'true' : undefined}
                        >
                          <span
                            style={
                              showLegend
                                ? getAnswerStatusColor(submissionObj?.scoreStatus)
                                : undefined
                            }
                          >
                            <CellAnswer
                              answer={answer}
                              dismissed={!!dismissed}
                            />
                          </span>
                        </StyledTableCell>
                      );
                    })
                  }
                </TableRow>
              ))
            }
          </TableBody>
        </Table>
      </TableContainer>
      {
        showLegend && <Legend />
      }
    </Box>
  );
};

export default AnswerSummary;
